import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

/**
 * Detects the presence of the Gloss Vault browser extension (see the
 * `gloss-extension` repo). The extension's content-script — injected only into
 * the Gloss web origins — announces itself with a `GLOSS_EXT_READY` message on
 * load and answers a `GLOSS_PING` with `GLOSS_PONG`. This service wraps both:
 * it listens for the announcement and can actively ping on demand, exposing a
 * single reactive "installed" flag the rest of the app gates on (e.g. the
 * grocery search, which needs the extension to fetch live prices).
 *
 * NB: the content-script only injects on page load, so installing the
 * extension while the app is open is reflected after the next reload — that's
 * the "…then reload this page" step in the install flow.
 */
@Injectable({ providedIn: "root" })
export class GlossExtensionService {
  /** Shared id used by the plugin-store entry and this service. */
  static readonly PLUGIN_ID = "gloss-vault-extension";

  private readonly installed$ = new BehaviorSubject<boolean>(false);

  constructor() {
    // Passive: the content-script fires this on every page load if installed.
    window.addEventListener("message", (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) {
        return;
      }
      const data = event.data as { __gloss?: boolean; type?: string };
      if (data?.__gloss === true && data.type === "GLOSS_EXT_READY") {
        this.installed$.next(true);
      }
    });
    // Active: catch the case where the announcement already fired before this
    // service subscribed (race on app start).
    void this.check();
  }

  /** Latest known installed state, synchronously. */
  isInstalled(): boolean {
    return this.installed$.value;
  }

  /** Emits the installed state and every change to it. */
  installedChanges(): Observable<boolean> {
    return this.installed$.asObservable();
  }

  /**
   * Actively pings the extension; resolves true if it answers within
   * `timeoutMs`. Updates {@link installedChanges} as a side effect so a
   * successful ping unblocks any gated UI. A ping getting no answer does NOT
   * flip a previously-true state back to false (the content-script may simply
   * be mid-navigation) — only an explicit announcement or ping proves presence.
   */
  check(timeoutMs = 1500): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const origin = window.location.origin;
      const reqId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;

      const timer = setTimeout(() => {
        window.removeEventListener("message", onMessage);
        resolve(false);
      }, timeoutMs);

      const onMessage = (event: MessageEvent): void => {
        if (event.source !== window || event.origin !== origin) {
          return;
        }
        const data = event.data as { __gloss?: boolean; type?: string; reqId?: string };
        if (data?.__gloss !== true || data.reqId !== reqId) {
          return;
        }
        if (data.type === "GLOSS_PONG") {
          clearTimeout(timer);
          window.removeEventListener("message", onMessage);
          this.installed$.next(true);
          resolve(true);
        }
      };

      window.addEventListener("message", onMessage);
      window.postMessage({ __gloss: true, type: "GLOSS_PING", reqId }, origin);
    });
  }
}
